export interface ApiErrorBody {
  error: string;
  code: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export const CSRF_COOKIE_NAME = 'chefcook_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';

export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const c of cookies) {
    const trimmed = c.trim();
    if (trimmed.startsWith(`${CSRF_COOKIE_NAME}=`)) {
      return decodeURIComponent(trimmed.slice(CSRF_COOKIE_NAME.length + 1));
    }
  }
  return null;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const csrf = getCsrfToken();
  if (csrf && method !== 'GET') {
    headers[CSRF_HEADER] = csrf;
  }
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  const text = await res.text();
  const data: unknown = text.length > 0 ? JSON.parse(text) : null;
  if (!res.ok) {
    const errBody = (data ?? {}) as Partial<ApiErrorBody>;
    const message = typeof errBody.error === 'string' ? errBody.error : res.statusText;
    const code = typeof errBody.code === 'string' ? errBody.code : 'HTTP_ERROR';
    throw new ApiError(res.status, code, message);
  }
  return data as T;
}

export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const csrf = getCsrfToken();
  if (csrf) {
    headers[CSRF_HEADER] = csrf;
  }
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
  });
  const text = await res.text();
  const data: unknown = text.length > 0 ? JSON.parse(text) : null;
  if (!res.ok) {
    const errBody = (data ?? {}) as Partial<ApiErrorBody>;
    const message = typeof errBody.error === 'string' ? errBody.error : res.statusText;
    const code = typeof errBody.code === 'string' ? errBody.code : 'HTTP_ERROR';
    throw new ApiError(res.status, code, message);
  }
  return data as T;
}

export function getDownloadUrl(path: string): string {
  return path;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}